import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  ChartBarIcon,
  ArrowsPointingOutIcon,
  PresentationChartBarIcon,
  PresentationChartLineIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { formatNumber, formatCurrency } from "../../utils/formatters";
import useTradingStore from "../../store/useTradingStore";
import { createPortal } from 'react-dom';

const TradingChartBackendAPI = ({ height = 400 }) => {
  const chartContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const [chartType, setChartType] = useState("candlestick");
  const [showVolume, setShowVolume] = useState(true);
  const [crosshair, setCrosshair] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
  const [timeframe, setTimeframe] = useState("1h");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const { selectedPair } = useTradingStore();

  useEffect(() => {
    const syncFullscreen = () => {
      setIsFullscreen(document.fullscreenElement === chartContainerRef.current);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setChartSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(chartContainerRef.current);
    return () => observer.disconnect();
  }, [fallbackFullscreen]);

  useEffect(() => {
    if (!fallbackFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const exitOnEscape = (event) => {
      if (event.key === 'Escape') {
        setFallbackFullscreen(false);
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', exitOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', exitOnEscape);
    };
  }, [fallbackFullscreen]);

  const handleFullscreen = async () => {
    if (fallbackFullscreen) {
      setFallbackFullscreen(false);
      setIsFullscreen(false);
      return;
    }
    try {
      if (document.fullscreenElement === chartContainerRef.current) {
        await document.exitFullscreen();
      } else {
        await chartContainerRef.current.requestFullscreen();
      }
    } catch (error) {
      // Embedded browsers may block the native Fullscreen API.
      setFallbackFullscreen(true);
      setIsFullscreen(true);
    }
  };

  // Backend API supported timeframes with appropriate days for data fetching
  const timeframes = [
    { value: "15m", label: "15m", days: 1 },
    { value: "30m", label: "30m", days: 2 },
    { value: "1h", label: "1h", days: 7 },
    { value: "4h", label: "4h", days: 30 },
    { value: "1d", label: "1d", days: 90 },
  ];

  // Fetch data from Backend API
  const fetchCoinData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const timeframeMeta = timeframes.find((tf) => tf.value === timeframe);

      // Call our backend Chart API
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const response = await fetch(
        `${baseUrl}/public/v1/chart-data/?pair=${selectedPair.replace(
          "/",
          "-"
        )}&timeframe=${timeframe}&days=${timeframeMeta.days}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Network error" }));
        throw new Error(
          errorData.error ||
            `HTTP ${response.status}: Failed to fetch chart data`
        );
      }

      const result = await response.json();

      // Backend API provides processed OHLC data directly
      if (!result.candles || !Array.isArray(result.candles)) {
        throw new Error("Invalid data format received from API");
      }

      setData(result.candles);
      setLastUpdate(new Date());
      console.log(
        `Loaded ${result.candles.length} candles for ${selectedPair} (${timeframe})`
      );
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError(err.message);
      // Clear data on error - no fallback to simulated data
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPair, timeframe]);

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchCoinData();
  }, [fetchCoinData, selectedPair]); // Add selectedPair as dependency

  // Auto-refresh based on timeframe
  useEffect(() => {
    let refreshInterval;

    // Set refresh intervals based on timeframe
    if (timeframe === "15m") {
      refreshInterval = 30000; // 30 seconds for 15-minute data
    } else if (["30m", "1h"].includes(timeframe)) {
      refreshInterval = 60000; // 1 minute for medium timeframes
    } else {
      // No auto-refresh for 4h and daily data
      return;
    }

    const interval = setInterval(fetchCoinData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchCoinData, timeframe]);

  // Process chart data
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        candles: [],
        volume: [],
        priceRange: { min: 0, max: 0 },
        volumeRange: { min: 0, max: 0 },
      };
    }

    const candles = data.map((item) => ({
      timestamp: item.timestamp || Date.now(),
      open: parseFloat(item.open || 0),
      high: parseFloat(item.high || 0),
      low: parseFloat(item.low || 0),
      close: parseFloat(item.close || 0),
      volume: parseFloat(item.volume || 0),
    }));

    const prices = candles
      .flatMap((c) => [c.open, c.high, c.low, c.close])
      .filter((p) => p > 0);
    const volumes = candles.map((c) => c.volume).filter((v) => v > 0);

    const priceRange = {
      min: Math.min(...prices) || 0,
      max: Math.max(...prices) || 0,
    };

    const volumeRange = {
      min: Math.min(...volumes) || 0,
      max: Math.max(...volumes) || 0,
    };

    return { candles, volume: volumes, priceRange, volumeRange };
  }, [data]);

  // Draw chart (same drawing logic as before but with enhanced styling)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !processedData.candles.length) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Set actual size in memory (scaled to account for extra pixel density)
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    // Scale the drawing context so everything will work at the correct size
    ctx.scale(dpr, dpr);

    const { width, height } = { width: rect.width, height: rect.height };
    const { candles, priceRange, volumeRange } = processedData;

    // Clear canvas with dark background
    ctx.fillStyle = "#0B1426";
    ctx.fillRect(0, 0, width, height);

    // Chart dimensions - enhanced responsive padding for small screens
    const isMobile = width < 768;
    const isSmallMobile = width < 480;
    const chartHeight = showVolume
      ? height * 0.75
      : height - (isSmallMobile ? 30 : isMobile ? 40 : 60);
    const volumeHeight = showVolume ? height * 0.2 : 0;
    const bottomPadding = isSmallMobile ? 20 : isMobile ? 25 : 35;
    const topPadding = isSmallMobile ? 15 : isMobile ? 20 : 30;
    const sidePadding = isSmallMobile ? 35 : isMobile ? 50 : 80;

    const chartWidth = width - sidePadding * 2;
    const candleWidth = Math.max(
      0.5,
      Math.min(4, chartWidth / candles.length - 1)
    );
    const candleSpacing = chartWidth / candles.length;

    // Price scale
    const priceScale =
      (chartHeight - topPadding - bottomPadding) /
      (priceRange.max - priceRange.min);
    const volumeScale = volumeHeight > 0 ? volumeHeight / volumeRange.max : 0;

    // Helper functions
    const getPriceY = (price) =>
      topPadding + (priceRange.max - price) * priceScale;
    const getVolumeY = (volume) =>
      chartHeight + 15 + volumeHeight - volume * volumeScale;
    const getCandleX = (index) => sidePadding + index * candleSpacing;

    // Draw grid with Binance-like styling
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1;

    // Horizontal grid lines - fewer on small screens
    const gridLines = isSmallMobile ? 3 : isMobile ? 4 : 8;
    for (let i = 0; i <= gridLines; i++) {
      const y =
        topPadding +
        (i * (chartHeight - topPadding - bottomPadding)) / gridLines;
      ctx.beginPath();
      ctx.moveTo(sidePadding, y);
      ctx.lineTo(width - sidePadding, y);
      ctx.stroke();

      // Price labels with enhanced responsive styling
      const price =
        priceRange.max - (i * (priceRange.max - priceRange.min)) / gridLines;
      ctx.fillStyle = "#64748B";
      ctx.font = isSmallMobile
        ? '8px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace'
        : isMobile
        ? '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace'
        : '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace';
      ctx.textAlign = "right";
      ctx.fillText(
        formatNumber(price, price > 1000 ? 0 : 4),
        sidePadding - 6,
        y + 3
      );
    }

    // Vertical grid lines - fewer on small screens
    const timeGridLines = isSmallMobile
      ? Math.min(3, candles.length)
      : isMobile
      ? Math.min(4, candles.length)
      : Math.min(8, candles.length);
    for (let i = 0; i <= timeGridLines; i++) {
      const x = sidePadding + (i * chartWidth) / timeGridLines;
      ctx.beginPath();
      ctx.moveTo(x, topPadding);
      ctx.lineTo(x, chartHeight);
      ctx.stroke();

      // Time labels with enhanced responsive styling
      if (i < candles.length) {
        const candleIndex = Math.floor((i * candles.length) / timeGridLines);
        if (candleIndex < candles.length) {
          const candle = candles[candleIndex];
          const date = new Date(candle.timestamp);
          const timeLabel =
            timeframe.includes("d") || timeframe.includes("w")
              ? date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
              : date.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                });

          ctx.fillStyle = "#64748B";
          ctx.font = isSmallMobile
            ? '7px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace'
            : isMobile
            ? '9px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace'
            : '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace';
          ctx.textAlign = "center";
          ctx.fillText(
            timeLabel,
            x,
            chartHeight + (isSmallMobile ? 15 : isMobile ? 20 : 25)
          );
        }
      }
    }

    // Draw candles or line chart with enhanced styling
    if (chartType === "candlestick") {
      candles.forEach((candle, index) => {
        const x = getCandleX(index);
        const openY = getPriceY(candle.open);
        const closeY = getPriceY(candle.close);
        const highY = getPriceY(candle.high);
        const lowY = getPriceY(candle.low);

        const isGreen = candle.close >= candle.open;
        const bodyHeight = Math.abs(closeY - openY);
        const bodyTop = Math.min(openY, closeY);

        // Draw wick with better colors
        ctx.strokeStyle = isGreen ? "#00C896" : "#FF6B7D";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + candleWidth / 2, highY);
        ctx.lineTo(x + candleWidth / 2, lowY);
        ctx.stroke();

        // Draw body with Binance-like colors
        if (bodyHeight < 1) {
          // Doji - draw thin line
          ctx.strokeStyle = isGreen ? "#00C896" : "#FF6B7D";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, bodyTop);
          ctx.lineTo(x + candleWidth, bodyTop);
          ctx.stroke();
        } else {
          ctx.fillStyle = isGreen ? "#00C896" : "#FF6B7D";
          ctx.fillRect(x, bodyTop, candleWidth, bodyHeight);
        }
      });
    } else if (chartType === "line") {
      // Enhanced line chart
      ctx.strokeStyle = "#F7931A";
      ctx.lineWidth = 2;
      ctx.beginPath();

      candles.forEach((candle, index) => {
        const x = getCandleX(index) + candleWidth / 2;
        const y = getPriceY(candle.close);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();

      // Add area fill
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = "#F7931A";
      ctx.lineTo(getCandleX(candles.length - 1) + candleWidth / 2, chartHeight);
      ctx.lineTo(sidePadding, chartHeight);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Draw volume bars with enhanced styling
    if (showVolume && volumeHeight > 0) {
      candles.forEach((candle, index) => {
        const x = getCandleX(index);
        const volumeY = getVolumeY(candle.volume);
        const isGreen = candle.close >= candle.open;

        ctx.fillStyle = isGreen ? "#00C89633" : "#FF6B7D33";
        ctx.fillRect(
          x,
          volumeY,
          candleWidth,
          chartHeight + 15 + volumeHeight - volumeY
        );
      });
    }

    // Draw crosshair with enhanced styling
    if (crosshair) {
      ctx.strokeStyle = "#94A3B8";
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(crosshair.x, topPadding);
      ctx.lineTo(crosshair.x, chartHeight);
      ctx.stroke();

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(sidePadding, crosshair.y);
      ctx.lineTo(width - sidePadding, crosshair.y);
      ctx.stroke();

      ctx.setLineDash([]);

      // Price label with enhanced responsive styling
      const price = priceRange.max - (crosshair.y - topPadding) / priceScale;
      const labelWidth = isSmallMobile ? 40 : isMobile ? 50 : 60;
      const labelHeight = isSmallMobile ? 16 : isMobile ? 18 : 20;

      ctx.fillStyle = "#1E293B";
      ctx.fillRect(
        width - sidePadding + 5,
        crosshair.y - labelHeight / 2,
        labelWidth,
        labelHeight
      );
      ctx.strokeStyle = "#64748B";
      ctx.strokeRect(
        width - sidePadding + 5,
        crosshair.y - labelHeight / 2,
        labelWidth,
        labelHeight
      );

      ctx.fillStyle = "#F1F5F9";
      ctx.font = isSmallMobile
        ? "8px monospace"
        : isMobile
        ? "10px monospace"
        : "11px monospace";
      ctx.textAlign = "center";
      ctx.fillText(
        formatNumber(price, price > 1000 ? 2 : 4),
        width - sidePadding + (isSmallMobile ? 25 : isMobile ? 30 : 35),
        crosshair.y + 3
      );
    }
  }, [processedData, chartType, showVolume, crosshair, isFullscreen, chartSize]);

  // Handle mouse events
  const handleMouseMove = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setCrosshair({ x, y });
  };

  const handleMouseLeave = () => {
    setCrosshair(null);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setShowMobileControls(false); // Close mobile controls after selection
  };

  const handleRefresh = () => {
    fetchCoinData();
  };

  // Calculate price statistics
  const currentPrice = data.length > 0 ? data[data.length - 1]?.close : 0;
  const previousPrice =
    data.length > 1 ? data[data.length - 2]?.close : currentPrice;
  const priceChange = currentPrice - previousPrice;
  const priceChangePercent = previousPrice
    ? (priceChange / previousPrice) * 100
    : 0;

  const chart = (
    <div ref={chartContainerRef} className="bg-gray-900 border border-gray-700 rounded-lg h-full flex flex-col"
      style={fallbackFullscreen ? { position: 'fixed', inset: 0, zIndex: 10000, width: '100vw', height: '100dvh', overflow: 'auto' } : undefined}>
      {/* Chart Header - Enhanced for small screens */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 border-b border-gray-700 space-y-2 sm:space-y-0">
        {/* Left Section */}
        <div className="flex items-center justify-between w-full sm:w-auto">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center space-x-2 font-bold">
              <span className="text-sm sm:text-base text-white">
                {selectedPair}
              </span>
            </div>
          </div>

          {/* Mobile Controls Toggle - Only show on small screens */}
          <div className="flex sm:hidden items-center space-x-2">
            <button
              onClick={() => setShowMobileControls(!showMobileControls)}
              className="p-1.5 text-white hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Chart Controls"
            >
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform ${
                  showMobileControls ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>

        {/* Right Section - Controls */}
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto ${
            showMobileControls ? "block" : "hidden sm:flex"
          }`}
        >
          {/* Timeframe Selector - Enhanced for mobile */}
          <div className="flex items-center space-x-1 bg-gray-800 rounded-lg p-1 overflow-x-auto w-full sm:w-auto">
            {timeframes.map((tf) => (
              <button
                key={tf.value}
                onClick={() => handleTimeframeChange(tf.value)}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded transition-colors flex-shrink-0 ${
                  timeframe === tf.value
                    ? "bg-[#0E1696] !text-white"
                    : "text-white hover:text-white hover:bg-gray-700"
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          {/* Chart Type Selector */}
          <div className="flex items-center space-x-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setChartType("candlestick")}
              className={`p-1.5 sm:p-2 rounded transition-colors ${
                chartType === "candlestick"
                  ? "bg-[#0E1696] !text-white"
                  : "text-white hover:text-white hover:bg-gray-700"
              }`}
              title="Candlestick Chart"
            >
              <PresentationChartBarIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
            <button
              onClick={() => setChartType("line")}
              className={`p-1.5 sm:p-2 rounded transition-colors ${
                chartType === "line"
                  ? "bg-[#0E1696] !text-white"
                  : "text-white hover:text-white hover:bg-gray-700"
              }`}
              title="Line Chart"
            >
              <PresentationChartLineIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>

          {/* Chart Controls */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={() => setShowVolume(!showVolume)}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded transition-colors ${
                showVolume
                  ? "bg-[#0E1696] !text-white"
                  : "bg-gray-800 text-white hover:text-white hover:bg-gray-700"
              }`}
            >
              Volume
            </button>

            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-1.5 sm:p-2 text-white hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
              title="Refresh Data"
            >
              <ArrowPathIcon
                className={`h-3 w-3 sm:h-4 sm:w-4 ${
                  loading ? "animate-spin" : ""
                }`}
              />
            </button>

            <button
              onClick={handleFullscreen}
              className="p-1.5 sm:p-2 text-white hover:text-white hover:bg-gray-700 rounded transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              aria-pressed={isFullscreen}
            >
              <ArrowsPointingOutIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Chart Canvas */}
      <div className="relative flex-1 min-h-[400px]">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="text-center text-white">
              <ChartBarIcon className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 opacity-50" />
              <p className="text-base sm:text-lg font-medium text-red-400">
                Failed to Load Chart Data
              </p>
              <p className="text-xs sm:text-sm mb-2">{error}</p>
              <p className="text-xs text-white mb-4">
                Pair: {selectedPair} | Timeframe: {timeframe}
              </p>
              <button
                onClick={handleRefresh}
                className="mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#0E1696] !text-white rounded hover:bg-blue-700 transition-colors text-xs sm:text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        ) : loading && data.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
              <span className="text-white text-sm sm:text-base">
                Loading {selectedPair} chart data...
              </span>
            </div>
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            className="w-full h-full cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </div>

      {/* Chart Info Footer - Enhanced for small screens */}
      {data.length > 0 && (
        <div className="px-3 sm:px-4 py-2 border-t border-gray-700 bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs text-white space-y-1 sm:space-y-0">
            <div className="flex flex-wrap items-center space-x-2 sm:space-x-4">
              <span>Candles: {data.length}</span>
              <span className="hidden sm:inline">Pair: {selectedPair}</span>
              <span>TF: {timeframe}</span>
              {lastUpdate && (
                <span className="hidden sm:inline">
                  Updated: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center space-x-2 sm:space-x-4">
              {data.length > 0 && (
                <>
                  <span className="hidden sm:inline">
                    Latest Vol:{" "}
                    {formatNumber(data[data.length - 1]?.volume || 0, 0)}
                  </span>
                  <span className="hidden sm:inline">
                    Range:{" "}
                    {formatNumber(Math.min(...data.map((d) => d.low)), 4)} -{" "}
                    {formatNumber(Math.max(...data.map((d) => d.high)), 4)}
                  </span>
                  <span
                    className={`${
                      data.length > 1 &&
                      data[data.length - 1]?.close >
                        data[data.length - 2]?.close
                        ? "text-white"
                        : data.length > 1 &&
                          data[data.length - 1]?.close <
                            data[data.length - 2]?.close
                        ? "text-red-400"
                        : "text-white"
                    }`}
                  >
                    {formatNumber(data[data.length - 1]?.close || 0, 4)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return fallbackFullscreen ? createPortal(chart, document.body) : chart;
};

export default TradingChartBackendAPI;
